import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Bell, Clock } from "lucide-react";
import { sr } from "../../lib/sr";

const RANGES = [
  { v: 24, label: "24h+" },
  { v: 48, label: "48h+" },
  { v: 72, label: "72h+" },
];

export default function OverdueApprovals() {
  const [hours, setHours] = useState(48);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = (h) => sr.overdueApprovals(h).then(setData).catch((e) => setData({ error: e?.response?.data?.detail || true }));
  useEffect(() => { load(hours); }, [hours]);

  const remind = async (id) => {
    setBusy(id);
    try { const r = await sr.remindApproval(id); toast.success(`Reminded ${r.reminded}${r.emailed ? ` · ${r.emailed} emailed` : ""}`); load(hours); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remind"); }
    finally { setBusy(null); }
  };
  const remindAll = async () => {
    setBusy("all");
    try { const r = await sr.remindAllOverdue(hours); toast.success(`Reminded ${r.campaigns_reminded} sign-off${r.campaigns_reminded === 1 ? "" : "s"} · ${r.emailed} emailed`); load(hours); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remind all"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">{typeof data.error === "string" ? data.error : "Could not load overdue approvals."}</p></div>;

  return (
    <div data-testid="overdue-page">
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><AlertTriangle size={12} /> Stuck sign-offs</span>
          <h1 style={{ fontSize: 30 }}>Overdue approvals</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Every campaign waiting too long on a client decision — nudge them in one tap.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select className="sr-select" value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="overdue-range" style={{ width: 120 }}>
            {RANGES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={remindAll} disabled={busy === "all" || data.total === 0} data-testid="remind-all-btn">
            {busy === "all" ? <Loader2 size={14} className="sr-spin" /> : <><Bell size={14} /> Remind all ({data.total})</>}
          </button>
        </div>
      </div>

      {data.total === 0 ? (
        <div className="sr-card" data-testid="overdue-empty"><p className="sr-muted" style={{ fontSize: 13 }}>Nothing is overdue past {hours}h. 🎉</p></div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {data.overdue.map((o) => (
            <div key={o.id} className="sr-card" data-testid={`overdue-${o.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
              <Clock size={16} style={{ color: "#C0362C", flexShrink: 0 }} />
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600, textTransform: "capitalize" }}>{o.campaign_name}</div>
                <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span>{o.client_name}</span>
                  <span className="sr-badge sr-badge-red" style={{ fontSize: 10 }}>waiting {Math.round(o.waiting_hours)}h</span>
                  {o.reminder_count > 0 && <span className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>reminded {o.reminder_count}×</span>}
                </div>
              </div>
              <Link to={`/app/campaigns/${o.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`overdue-open-${o.id}`}>Open</Link>
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => remind(o.id)} disabled={busy === o.id} data-testid={`overdue-remind-${o.id}`}>
                {busy === o.id ? <Loader2 size={14} className="sr-spin" /> : <><Bell size={14} /> Remind</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
